import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { login, register, isAuthenticated } from '../services/authService';

const AuthPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'login' | 'register'>('login');
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [registerData, setRegisterData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Check if user is already authenticated
  useEffect(() => {
    if (isAuthenticated()) {
      navigate('/');
    }

    // Check if there's a tab preference in sessionStorage
    const storedTab = sessionStorage.getItem('authTab');
    if (storedTab === 'register') {
      setActiveTab('register');
      sessionStorage.removeItem('authTab'); // Clear after use
    }
  }, [navigate]);

  const handleLoginChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLoginData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null);
  };

  const handleRegisterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setRegisterData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null);
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginData.username || !loginData.password) {
      setError('All fields are required');
      return;
    }

    setLoading(true);
    try {
      await login(loginData);
      setSuccess('Login successful! Redirecting...');
      setTimeout(() => {
        navigate('/');
      }, 1000);
    } catch (err) {
      console.error('Login error:', err);
      setError('Invalid username or password');
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!registerData.username || !registerData.email || !registerData.password || !registerData.confirmPassword) {
      setError('All fields are required');
      return;
    }

    if (registerData.password !== registerData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await register({
        username: registerData.username,
        email: registerData.email,
        password: registerData.password
      });
      setSuccess('Registration successful! Please login.');
      setTimeout(() => {
        setActiveTab('login');
      }, 1000);
    } catch (err) {
      console.error('Registration error:', err);
      setError('Registration failed. Username or email may be taken.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative overflow-hidden min-h-screen" style={{
      background: 'linear-gradient(to bottom, #141019, #1A1625)',
    }}>
      <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.03]"></div>
      <div className="absolute top-0 right-0 w-96 h-96 bg-purple-500/10 blur-[150px] rounded-full"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-pink-500/10 blur-[150px] rounded-full"></div>

      <div className="container mx-auto py-12 px-4 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-md mx-auto bg-gradient-to-r from-[#1F1A27] to-[#2A1E44] rounded-lg shadow-lg overflow-hidden border border-white/5"
        >
          <div className="flex border-b border-white/10">
            <button
              className={`flex-1 py-4 font-medium text-center transition-colors ${activeTab === 'login' ? 'text-[#F670C7] border-b-2 border-[#F670C7]' : 'text-white/60 hover:text-white/80'}`}
              onClick={() => setActiveTab('login')}
            >
              Login
            </button>
            <button
              className={`flex-1 py-4 font-medium text-center transition-colors ${activeTab === 'register' ? 'text-[#F670C7] border-b-2 border-[#F670C7]' : 'text-white/60 hover:text-white/80'}`}
              onClick={() => setActiveTab('register')}
            >
              Register
            </button>
          </div>

          <div className="p-8">
            {error && (
              <div className="bg-red-900/20 border border-red-500/30 text-red-400 px-4 py-3 rounded mb-4">
                {error}
              </div>
            )}

            {success && (
              <div className="bg-green-900/20 border border-green-500/30 text-green-400 px-4 py-3 rounded mb-4">
                {success}
              </div>
            )}

            {activeTab === 'login' ? (
              <form onSubmit={handleLoginSubmit}>
                <div className="mb-4">
                  <label htmlFor="username" className="block text-white/80 mb-2">Username</label>
                  <input
                    type="text"
                    id="username"
                    name="username"
                    value={loginData.username}
                    onChange={handleLoginChange}
                    className="w-full px-4 py-2 bg-[#2A1E44]/50 border border-white/10 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F670C7] text-white"
                    required
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="password" className="block text-white/80 mb-2">Password</label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={loginData.password}
                    onChange={handleLoginChange}
                    className="w-full px-4 py-2 bg-[#2A1E44]/50 border border-white/10 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F670C7] text-white"
                    required
                  />
                </div>

                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="remember"
                      className="h-4 w-4 text-[#F670C7] bg-[#2A1E44]/50 border-white/10 rounded focus:ring-[#F670C7]"
                    />
                    <label htmlFor="remember" className="ml-2 block text-sm text-white/70">
                      Remember me
                    </label>
                  </div>

                  <a href="#" className="text-sm text-[#F670C7] hover:underline">
                    Forgot password?
                  </a>
                </div>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  className="w-full bg-gradient-to-r from-[#7F56D9] to-[#F670C7] text-white px-6 py-3 rounded-md hover:opacity-90 transition duration-300"
                  disabled={loading}
                >
                  {loading ? 'Signing In...' : 'Sign In'}
                </motion.button>
              </form>
            ) : (
              <form onSubmit={handleRegisterSubmit}>
                <div className="mb-4">
                  <label htmlFor="username" className="block text-white/80 mb-2">Username</label>
                  <input
                    type="text"
                    id="username"
                    name="username"
                    value={registerData.username}
                    onChange={handleRegisterChange}
                    className="w-full px-4 py-2 bg-[#2A1E44]/50 border border-white/10 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F670C7] text-white"
                    required
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="reg-email" className="block text-white/80 mb-2">Email</label>
                  <input
                    type="email"
                    id="reg-email"
                    name="email"
                    value={registerData.email}
                    onChange={handleRegisterChange}
                    className="w-full px-4 py-2 bg-[#2A1E44]/50 border border-white/10 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F670C7] text-white"
                    required
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="reg-password" className="block text-white/80 mb-2">Password</label>
                  <input
                    type="password"
                    id="reg-password"
                    name="password"
                    value={registerData.password}
                    onChange={handleRegisterChange}
                    className="w-full px-4 py-2 bg-[#2A1E44]/50 border border-white/10 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F670C7] text-white"
                    required
                  />
                </div>

                <div className="mb-6">
                  <label htmlFor="confirm-password" className="block text-white/80 mb-2">Confirm Password</label>
                  <input
                    type="password"
                    id="confirm-password"
                    name="confirmPassword"
                    value={registerData.confirmPassword}
                    onChange={handleRegisterChange}
                    className="w-full px-4 py-2 bg-[#2A1E44]/50 border border-white/10 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F670C7] text-white"
                    required
                  />
                </div>

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  className="w-full bg-gradient-to-r from-[#7F56D9] to-[#F670C7] text-white px-6 py-3 rounded-md hover:opacity-90 transition duration-300"
                  disabled={loading}
                >
                  {loading ? 'Creating Account...' : 'Create Account'}
                </motion.button>
              </form>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AuthPage;
